import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Card as MuiCard,
  Button,
  Grid,
  IconButton,
  Tabs,
  Tab,
  TextField,
} from "@material-ui/core";
import { useParams, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/store";
import { getProgramById } from "../../redux/actions/programAction";
import useStyles from "../../containers/Auth/Onboarding/style";
import backIcon from "../../assets/icons/backIcon.svg";
import appLogo from "../../assets/logo.png";
import tulips from "../../assets/themeImages/tulips.jpg";
import sunset from "../../assets/themeImages/sunset.png";
import ocean from "../../assets/themeImages/ocean.png";
import medical from "../../assets/themeImages/medical.png";
import golf from "../../assets/themeImages/golf.png";
import forrest from "../../assets/themeImages/forrest.png";
import { assetUrl } from "../../util/assetUrl";
import { INSTANCE } from "../../api/endpoint";
import Cropper from "react-easy-crop";

// === Config ===
const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:9090";
const ASSET_BASE = process.env.REACT_APP_ASSET_BASE_URL || API_BASE;
const HEADER_UPLOAD_ENDPOINT = `${API_BASE}/v1/users/upload/community-header`;

// Preset themes
const PRESET_THEMES = [
  { id: "default", name: "Default Blue", mainColor: "#1976d2", secondaryColor: "#ffffff", headerImage: "", fontStyle: "Arial" },
  { id: "dark", name: "Dark Mode", mainColor: "#bb86fc", secondaryColor: "#121212", headerImage: "", fontStyle: "Arial" },
  { id: "sports", name: "Sports", mainColor: "#4caf50", secondaryColor: "#e8f5e8", headerImage: golf, fontStyle: "Arial" },
  { id: "red-flower", name: "Red Flower", mainColor: "#e91e63", secondaryColor: "#fce4ec", headerImage: tulips, fontStyle: "Arial" },
  { id: "ocean", name: "Ocean Blue", mainColor: "#0277bd", secondaryColor: "#e1f5fe", headerImage: ocean, fontStyle: "Arial" },
  { id: "sunset", name: "Sunset Orange", mainColor: "#ff6f00", secondaryColor: "#fff3e0", headerImage: sunset, fontStyle: "Arial" },
  { id: "forest", name: "Forest Green", mainColor: "#2e7d32", secondaryColor: "#e8f5e8", headerImage: forrest, fontStyle: "Arial" },
  { id: "purple", name: "Royal Purple", mainColor: "#7b1fa2", secondaryColor: "#f3e5f5", headerImage: "", fontStyle: "Arial" },
  { id: "corporate", name: "Corporate Gray", mainColor: "#37474f", secondaryColor: "#eceff1", headerImage: "", fontStyle: "Arial" },
  { id: "medical", name: "Medical", mainColor: "#00796b", secondaryColor: "#e0f2f1", headerImage: medical, fontStyle: "Arial" },
];

// === Helpers ===
const isBlobUrl = (u?: string) => !!u && u.startsWith("blob:");
function invertColor(hex: string) {
  let col = (hex || "#ffffff").replace(/^#/, "");
  if (col.length === 3) col = col.split("").map((c) => c + c).join("");
  const num = parseInt(col || "ffffff", 16);
  const r = 255 - ((num >> 16) & 0xff);
  const g = 255 - ((num >> 8) & 0xff);
  const b = 255 - (num & 0xff);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

// Resolve stored or imported paths using ASSET_BASE
const resolveHeaderUrl = (p: string): string => {
  if (!p) return "";
  if (/^(blob:|data:|https?:\/\/)/i.test(p)) return p;
  if (p.startsWith("/assets") || p.startsWith("/uploads")) return `${ASSET_BASE}${p}`;
  try {
    return assetUrl(p);
  } catch {
    return p;
  }
};

/* ===== tiny theme comparison helpers ===== */
type Theme = {
  mainColor?: string;
  secondaryColor?: string;
  headerImage?: string;
  fontStyle?: string;
};

const normalizeTheme = (t?: Theme) => ({
  mainColor: t?.mainColor || "",
  secondaryColor: t?.secondaryColor || "",
  headerImage: t?.headerImage || "",
  fontStyle: t?.fontStyle || "",
});

const themesEqual = (a?: Theme, b?: Theme) => {
  const A = normalizeTheme(a);
  const B = normalizeTheme(b);
  return (
    A.mainColor === B.mainColor &&
    A.secondaryColor === B.secondaryColor &&
    A.headerImage === B.headerImage &&
    A.fontStyle === B.fontStyle
  );
};

const isEmptyTheme = (t?: Theme) => {
  const T = normalizeTheme(t);
  return !T.mainColor && !T.secondaryColor && !T.headerImage && !T.fontStyle;
};

const isInherited = (channelStyle?: Theme, prevCommunity?: Theme) =>
  isEmptyTheme(channelStyle) || themesEqual(channelStyle, prevCommunity);

const channelStyleFromCommunity = (cs: {
  mainColor?: string;
  secondaryColor?: string;
  headerImage?: string;
  fontStyle?: string;
}) => ({
  mainColor: cs?.mainColor || "",
  secondaryColor: cs?.secondaryColor || "",
  headerImage: cs?.headerImage || "",
  fontStyle: cs?.fontStyle || "Arial",
});
/* ======================================== */

type UrlParams = { programId?: string; communityIdentifier: string };

export default function AdminCommunityThemeEditor() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { programId: urlProgramId, communityIdentifier } = useParams<UrlParams>();

  const reduxProgram = useSelector((s: RootState) => s.programReducer.singleProgram);
  const programId = urlProgramId || reduxProgram?._id;

  // local theme state (communityStyle shape + logo)
  const [communityStyle, setCommunityStyle] = useState({
    mainColor: "#1976d2",
    secondaryColor: "#ffffff",
    headerImage: "",
    fontStyle: "Arial",
    logo: "",
  });

  const [selectedThemeId, setSelectedThemeId] = useState("default");
  const [themeTab, setThemeTab] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>("");

  // Header crop/upload state
  const [cropOpen, setCropOpen] = useState(false);
  const [cropImgSrc, setCropImgSrc] = useState<string>("");
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const headerFileInputRef = useRef<HTMLInputElement | null>(null);
  const [headerUploading, setHeaderUploading] = useState(false);
  const [headerUploadMsg, setHeaderUploadMsg] = useState<string>("");
  const [headerBust, setHeaderBust] = useState<number>(0);
  const headerBlobUrlRef = useRef<string | null>(null);

  // Logo upload state
  const logoFileInputRef = useRef<HTMLInputElement | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUploadMsg, setLogoUploadMsg] = useState<string>("");
  const [logoBust, setLogoBust] = useState<number>(0);
  const logoBlobUrlRef = useRef<string | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string>("");

  const isMounted = useRef(true);
  useEffect(
    () => () => {
      isMounted.current = false;
      if (headerBlobUrlRef.current) {
        URL.revokeObjectURL(headerBlobUrlRef.current);
        headerBlobUrlRef.current = null;
      }
      if (logoBlobUrlRef.current) {
        URL.revokeObjectURL(logoBlobUrlRef.current);
        logoBlobUrlRef.current = null;
      }
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    },
    [logoPreviewUrl]
  );

  useEffect(() => {
    if (programId && !reduxProgram) dispatch(getProgramById(programId));
  }, [programId, reduxProgram, dispatch]);

  const community = useMemo(
    () => reduxProgram?.communities?.find((c: any) => c.communityIdentifier === communityIdentifier),
    [reduxProgram, communityIdentifier]
  );

  useEffect(() => {
    if (!community) return;
    const current = {
      mainColor: community.communityStyle?.mainColor || "#1976d2",
      secondaryColor: community.communityStyle?.secondaryColor || "#ffffff",
      headerImage: community.communityStyle?.headerImage || "",
      fontStyle: community.communityStyle?.fontStyle || "Arial",
      logo: community.communityStyle?.logo || "",
    };
    setCommunityStyle(current);
    setHeaderBust(Date.now());
    setLogoBust(Date.now());
    setLogoPreviewUrl("");

    const match = PRESET_THEMES.find(
      (t) =>
        t.mainColor === current.mainColor &&
        t.secondaryColor === current.secondaryColor &&
        t.headerImage === current.headerImage &&
        t.fontStyle === current.fontStyle
    );
    if (match) {
      setSelectedThemeId(match.id);
      setThemeTab(0);
    } else {
      setSelectedThemeId("custom");
      setThemeTab(1);
    }
  }, [community]);

  const invertedSecondary = useMemo(
    () => invertColor(communityStyle.secondaryColor),
    [communityStyle.secondaryColor]
  );

  const headerImageUrl = useMemo(() => {
    const u = resolveHeaderUrl(communityStyle.headerImage);
    if (!u) return "";
    return headerBust ? `${u}${u.includes("?") ? "&" : "?"}v=${headerBust}` : u;
  }, [communityStyle.headerImage, headerBust]);

  const logoImageUrl = useMemo(() => {
    const u = resolveHeaderUrl(communityStyle.logo);
    if (!u) return "";
    return logoBust ? `${u}${u.includes("?") ? "&" : "?"}v=${logoBust}` : u;
  }, [communityStyle.logo, logoBust]);

  const hasCustomLogo = Boolean(logoPreviewUrl || (communityStyle.logo && !communityStyle.logo.startsWith("blob:")));
  const idsReady = !!programId && !!communityIdentifier;

  // File → open crop modal
  const onHeaderFilePicked: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!idsReady) {
      setHeaderUploadMsg("Program/community not loaded yet.");
      if (headerFileInputRef.current) headerFileInputRef.current.value = "";
      return;
    }
    const fr = new FileReader();
    fr.onload = () => {
      setCropImgSrc(String(fr.result || ""));
      setCropOpen(true);
      setHeaderUploadMsg("");
    };
    fr.readAsDataURL(file);
    if (headerFileInputRef.current) headerFileInputRef.current.value = "";
  };

  // Crop util
  const getCroppedBlob = async (imageSrc: string, cropPx: any): Promise<Blob> => {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.crossOrigin = "anonymous";
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = imageSrc;
    });
    const canvas = document.createElement("canvas");
    canvas.width = 1400;
    canvas.height = Math.round(canvas.width / 3.5);
    const ctx = canvas.getContext("2d")!;

    const temp = document.createElement("canvas");
    temp.width = cropPx.width;
    temp.height = cropPx.height;
    const tctx = temp.getContext("2d")!;
    tctx.drawImage(
      img,
      cropPx.x, cropPx.y, cropPx.width, cropPx.height,
      0, 0, cropPx.width, cropPx.height
    );
    ctx.drawImage(temp, 0, 0, canvas.width, canvas.height);

    return await new Promise<Blob>((resolve) =>
      canvas.toBlob((b) => resolve(b || new Blob()), "image/jpeg", 0.92)
    );
  };

  // Crop modal
  const CropModal = cropOpen ? (
    <div
      style={{
        position: "fixed",
        top: 0, left: 0, right: 0, bottom: 0,
        background: "#000A",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1300
      }}
    >
      <div
        style={{
          width: "min(900px, 96vw)", height: "min(420px, 70vh)",
          background: communityStyle.secondaryColor, borderRadius: 12, overflow: "hidden",
          boxShadow: "0 20px 40px rgba(0,0,0,.4)", display: "flex", flexDirection: "column"
        }}
      >
        <div style={{ position: "relative", flex: 1, background: "#111" }}>
          <Cropper
            image={cropImgSrc}
            crop={crop}
            zoom={zoom}
            aspect={3.5}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(_, px) => setCroppedAreaPixels(px)}
            restrictPosition
            showGrid={false}
          />
        </div>

        <div style={{ display: "flex", gap: 12, padding: 12, alignItems: "center" }}>
          <Typography variant="body2" style={{ flex: 1, color: invertedSecondary }}>
            Drag to position. Zoom to adjust framing.
          </Typography>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
            style={{ width: 180 }}
          />
          <Button onClick={() => setCropOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={async () => {
              if (!croppedAreaPixels || !cropImgSrc) {
                setHeaderUploadMsg("Select an area to crop first.");
                return;
              }
              if (!idsReady) {
                setHeaderUploadMsg("Missing program/community identifiers.");
                return;
              }

              try {
                setHeaderUploading(true);
                setHeaderUploadMsg("Uploading header…");

                const blob = await getCroppedBlob(cropImgSrc, croppedAreaPixels);
                const name = "default-header.jpg";

                // Instant local preview
                const localUrl = URL.createObjectURL(blob);
                if (headerBlobUrlRef.current) URL.revokeObjectURL(headerBlobUrlRef.current);
                headerBlobUrlRef.current = localUrl;
                setSelectedThemeId("custom");
                setThemeTab(1);
                setCommunityStyle((s) => ({ ...s, headerImage: localUrl }));
                setHeaderBust(0);

                // Upload to backend (community-level route)
                const fd = new FormData();
                fd.append("file", blob, name);
                fd.append("programId", String(programId));
                fd.append("communityIdentifier", communityIdentifier);

                const url = new URL(HEADER_UPLOAD_ENDPOINT);
                url.searchParams.set("programId", String(programId));
                url.searchParams.set("communityIdentifier", communityIdentifier);

                const token = localStorage.getItem("token") || "";
                const csrf =
                  localStorage.getItem("csrfToken") ||
                  localStorage.getItem("XSRF-TOKEN") ||
                  "";

                const resp = await fetch(url.toString(), {
                  method: "POST",
                  headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    ...(csrf ? { "x-csrf-token": csrf } : {}),
                  },
                  body: fd,
                  credentials: "include",
                });

                const raw = await resp.text();
                let out: any = {};
                try { out = raw ? JSON.parse(raw) : {}; } catch {}

                if (!resp.ok) {
                  const msg =
                    out?.message ||
                    out?.error?.message ||
                    out?.error ||
                    `Upload failed (${resp.status}${resp.statusText ? ` ${resp.statusText}` : ""})`;
                  throw new Error(msg);
                }

                const relUrl: string =
                  out?.data?.url ||
                  out?.url ||
                  out?.fileName ||
                  out?.location ||
                  out?.path ||
                  "";

                if (!relUrl || isBlobUrl(relUrl)) {
                  throw new Error("Upload succeeded but no server URL was returned.");
                }

                // Swap to server URL
                setCommunityStyle((s) => ({ ...s, headerImage: relUrl }));
                setHeaderBust(Date.now());
                setHeaderUploadMsg("Header image updated.");

                // Clean up blob
                if (headerBlobUrlRef.current) {
                  URL.revokeObjectURL(headerBlobUrlRef.current);
                  headerBlobUrlRef.current = null;
                }
              } catch (err) {
                console.error(err);
                setHeaderUploadMsg((err as any)?.message || "Upload failed.");
              } finally {
                setHeaderUploading(false);
                setCropOpen(false);
              }
            }}
          >
            Save Crop
          </Button>
        </div>
      </div>
    </div>
  ) : null;

  // Logo picker: local preview → upload → set REAL url
  const onLogoFilePicked: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !programId) return;

    const inputEl = e.currentTarget;

    try {
      setLogoUploading(true);
      setLogoUploadMsg("");

      const localUrl = URL.createObjectURL(file);
      if (logoBlobUrlRef.current) URL.revokeObjectURL(logoBlobUrlRef.current);
      logoBlobUrlRef.current = localUrl;
      setLogoPreviewUrl(localUrl);

      const fd = new FormData();
      fd.append("file", file, "header.png");
      fd.append("programId", String(programId));
      fd.append("communityIdentifier", communityIdentifier);

      const url = new URL(`${API_BASE}/v1/users/upload/community-logo`);
      url.searchParams.set("programId", String(programId));
      url.searchParams.set("communityIdentifier", communityIdentifier);

      const token = localStorage.getItem("token") || "";
      const csrf = localStorage.getItem("csrfToken") || localStorage.getItem("XSRF-TOKEN") || "";

      const resp = await fetch(url.toString(), {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(csrf ? { "x-csrf-token": csrf } : {}),
        },
        body: fd,
        credentials: "include",
      });

      const raw = await resp.text();
      let out: any = {};
      try { out = raw ? JSON.parse(raw) : {}; } catch {}

      if (!resp.ok) {
        const msg = out?.message || out?.error?.message || out?.error || `Upload failed (${resp.status}${resp.statusText ? ` ${resp.statusText}` : ""})`;
        throw new Error(msg);
      }

      const relUrl: string =
        out?.data?.url ||
        out?.url ||
        out?.fileName ||
        out?.location ||
        out?.path ||
        "";

      if (!relUrl) throw new Error("Upload succeeded but no URL was returned.");

      setCommunityStyle((s) => ({ ...s, logo: relUrl }));
      setLogoBust(Date.now());
      setLogoUploadMsg("Logo updated.");

      setLogoPreviewUrl("");
      if (logoBlobUrlRef.current) {
        URL.revokeObjectURL(logoBlobUrlRef.current);
        logoBlobUrlRef.current = null;
      }
    } catch (err) {
      setLogoUploadMsg((err as any)?.message || "Upload failed.");
    } finally {
      setLogoUploading(false);
      if (inputEl) inputEl.value = "";
    }
  };

  // Remove custom logo → fall back to appLogo (fix: bump cache & clear preview reliably)
  const handleRemoveLogo = () => {
  try {
    if (logoBlobUrlRef.current) {
      URL.revokeObjectURL(logoBlobUrlRef.current);
      logoBlobUrlRef.current = null;
    }
  } catch {}
  setLogoPreviewUrl("");
  setCommunityStyle((s) => ({ ...s, logo: "" }));
  setLogoBust(Date.now()); // force <img> to re-resolve to appLogo
  setLogoUploadMsg("Custom logo removed. Using default app logo.");
};


  // Save Program with updated communityStyle
  const handleSave = async () => {
    if (!programId || !community || !reduxProgram) return;
    setSaving(true);
    setSaveError("");

    // Prevent saving while header is still a blob preview or uploading
    if (headerUploading || isBlobUrl(communityStyle.headerImage)) {
      setSaving(false);
      setSaveError("Please wait for the header upload to finish.");
      return;
    }

    const prevCommunityStyle: Theme = {
      mainColor: community?.communityStyle?.mainColor,
      secondaryColor: community?.communityStyle?.secondaryColor,
      headerImage: community?.communityStyle?.headerImage,
      fontStyle: community?.communityStyle?.fontStyle,
    };

    const updatedProgram = {
      ...reduxProgram,
      communities: reduxProgram.communities.map((c: any) =>
        c.communityIdentifier === communityIdentifier
          ? {
              ...c,
              communityStyle: {
                ...(c.communityStyle || {}),
                mainColor: communityStyle.mainColor,
                secondaryColor: communityStyle.secondaryColor,
                headerImage: communityStyle.headerImage, // server URL or empty
                fontStyle: communityStyle.fontStyle,
                logo: communityStyle.logo,
              },
            }
          : c
      ),
    };

    const getChannelsForCommunity = (): any[] => {
      const nested = community?.channels;
      if (Array.isArray(nested) && nested.length) return nested;
      const byArr = (arr?: any[]) =>
        (arr || []).filter((ch) => ch?.communityIdentifier === communityIdentifier);
      if (Array.isArray((reduxProgram as any).channels)) return byArr((reduxProgram as any).channels);
      if (Array.isArray((reduxProgram as any).channelList)) return byArr((reduxProgram as any).channelList);
      if (Array.isArray((reduxProgram as any).enrolledChannels)) return byArr((reduxProgram as any).enrolledChannels);
      return [];
    };

    const channelsInCommunity = getChannelsForCommunity();
    const channelsToWrite = channelsInCommunity.filter((ch) =>
      isInherited(ch?.channelStyle, prevCommunityStyle)
    );
    const newChanStyle = channelStyleFromCommunity(communityStyle);

    try {
      // optimistic store updates
      dispatch({ type: "GET_SINGLE_PROGRAM", payload: updatedProgram });
      dispatch({
        type: "SET_COMMUNITY_THEME",
        communityId: communityIdentifier,
        payload: { ...communityStyle },
      });

      // save program
      await INSTANCE.put(`/v1/programs/${programId}`, updatedProgram);

      // push theme to inherited channels
      if (channelsToWrite.length > 0) {
        await Promise.all(
          channelsToWrite.map(async (ch) => {
            try {
              await INSTANCE.put(`/v1/channels/${ch._id}`, {
                ...ch,
                channelStyle: { ...newChanStyle },
              });
              dispatch({
                type: "SET_CHANNEL_THEME",
                channelId: ch?.channelIdentifier || ch?._id,
                payload: { ...newChanStyle },
              });
            } catch (e) {
              const status = (e as any)?.response?.status;
              if (status !== 404) {
                console.warn("Channel theme write failed for", ch?._id, e);
              }
            }
          })
        );

        const patchChannels = (arr?: any[]) =>
          Array.isArray(arr)
            ? arr.map((ch) =>
                channelsToWrite.some((x) => x?._id === ch?._id)
                  ? { ...ch, channelStyle: { ...newChanStyle } }
                  : ch
              )
            : arr;

        const programWithPatchedChannels = {
          ...updatedProgram,
          communities: (updatedProgram.communities || []).map((c: any) =>
            c.communityIdentifier === communityIdentifier
              ? { ...c, channels: patchChannels(c.channels) }
              : c
          ),
          channels: patchChannels((updatedProgram as any).channels),
          channelList: patchChannels((updatedProgram as any).channelList),
          enrolledChannels: patchChannels((updatedProgram as any).enrolledChannels),
        };

        dispatch({ type: "GET_SINGLE_PROGRAM", payload: programWithPatchedChannels });
      }

      history.go(-1);
    } catch (err) {
      console.error(err);
      const msg =
        (err as any)?.response?.data?.message ||
        (err as any)?.message ||
        "Failed to save changes.";
      setSaveError(msg);
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  const getThemeById = (id: string) => PRESET_THEMES.find((t) => t.id === id);
  const isPresetId = (id: string) => !!getThemeById(id);

  const handleThemeSelect = (themeId: string) => {
    const theme = PRESET_THEMES.find((t) => t.id === themeId);
    if (theme) {
      setSelectedThemeId(themeId);
      setThemeTab(0);
      setCommunityStyle((s) => ({
        ...s,
        mainColor: theme.mainColor,
        secondaryColor: theme.secondaryColor,
        headerImage: theme.headerImage,
        fontStyle: theme.fontStyle,
      }));
      setHeaderBust(0);
    }
  };

  if (!reduxProgram || !community) {
    const invertedSecondary = invertColor(communityStyle.secondaryColor);
    return (
      <Box
        style={{ backgroundColor: communityStyle.secondaryColor, color: invertedSecondary }}
        className={classes.container}
        textAlign="center"
        mt={10}
      >
        <Typography style={{ color: invertedSecondary }}>Loading community data…</Typography>
      </Box>
    );
  }

  return (
    <>
      {CropModal}
      <div
        className={classes.container}
        style={{ backgroundColor: communityStyle.secondaryColor, color: invertedSecondary }}
      >
        <MuiCard
          className={classes.card}
          style={{
            backgroundColor: communityStyle.secondaryColor,
            border: `1px solid ${invertedSecondary}`,
            boxShadow: `0 8px 24px ${invertedSecondary}33`,
          }}
        >
          <Box
            className={classes.header}
            style={{ backgroundColor: communityStyle.mainColor, color: communityStyle.secondaryColor }}
          >
            <IconButton onClick={() => history.go(-1)}>
              <img src={backIcon} alt="Back" />
            </IconButton>
            <Box className={classes.logoWrapper}>
              <img
                src={logoPreviewUrl || logoImageUrl || appLogo}
                alt="Logo"
                className={classes.logo}
              />
            </Box>
          </Box>

          <Typography component="h1" className={classes.onboardingTitle} style={{ color: communityStyle.mainColor }}>
            Community Theme
          </Typography>
          <Typography component="h2" className={classes.onboardingHeading} style={{ color: invertedSecondary }}>
            {communityIdentifier}
          </Typography>

          {/* COMMUNITY LOGO */}
          <Box mt={2} p={2} style={{ border: `1px dashed ${invertedSecondary}55`, borderRadius: 8 }}>
            <Typography variant="body2" style={{ color: `${invertedSecondary}cc`, fontWeight: 600 }}>
              Community Logo
            </Typography>

            <Box mt={1} display="flex" alignItems="center" style={{ gap: 12, flexWrap: "wrap" }}>
              <input
                ref={logoFileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={onLogoFilePicked}
              />

              <Button
                variant="outlined"
                onClick={() => logoFileInputRef.current?.click()}
                disabled={logoUploading}
                style={{ color: communityStyle.mainColor, borderColor: communityStyle.mainColor }}
              >
                {logoUploading ? "Uploading…" : "Choose Logo Image"}
              </Button>

              <Button
                variant="text"
                onClick={handleRemoveLogo}
                disabled={!hasCustomLogo || logoUploading}
                style={{ color: `${invertedSecondary}CC` }}
              >
                Remove Custom Logo
              </Button>

              <div
                style={{
                  width: 320,
                  height: 140,
                  borderRadius: 12,
                  overflow: "hidden",
                  border: `1px solid ${invertedSecondary}33`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: `${invertedSecondary}0D`,
                  boxShadow: `0 4px 10px ${invertedSecondary}22`,
                }}
              >
                <img
                  src={logoPreviewUrl || logoImageUrl || resolveHeaderUrl(appLogo)}
                  alt="logo preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                    background: communityStyle.secondaryColor,
                  }}
                />
              </div>
            </Box>

            {logoUploadMsg && (
              <Typography variant="caption" style={{ color: `${invertedSecondary}99` }}>
                {logoUploadMsg}
              </Typography>
            )}
          </Box>

          {/* THEME TABS */}
          <Box mt={4}>
            <Typography variant="h6" style={{ color: invertedSecondary, marginBottom: 8 }}>
              Theme
            </Typography>

            <Tabs
              value={themeTab}
              onChange={(_, v) => {
                setThemeTab(v);
                if (v === 0) {
                  if (!isPresetId(selectedThemeId)) {
                    const fallback = getThemeById("default") || PRESET_THEMES[0];
                    setSelectedThemeId(fallback.id);
                    setCommunityStyle((s) => ({
                      ...s,
                      mainColor: fallback.mainColor,
                      secondaryColor: fallback.secondaryColor,
                      headerImage: fallback.headerImage,
                      fontStyle: fallback.fontStyle,
                    }));
                    setHeaderBust(0);
                  }
                } else {
                  if (isPresetId(selectedThemeId)) {
                    setSelectedThemeId("custom");
                  }
                }
              }}
              indicatorColor="primary"
              textColor="primary"
              style={{ marginBottom: 16, borderBottom: `1px solid ${invertedSecondary}33` }}
            >
              <Tab label="Default" />
              <Tab label="Custom" />
            </Tabs>

            {/* DEFAULT THEMES GRID */}
            {themeTab === 0 && (
              <Grid container spacing={2}>
                {PRESET_THEMES.map((theme) => {
                  const isSelected = selectedThemeId === theme.id;
                  const themeCardStyle = {
                    height: 80,
                    cursor: "pointer",
                    border: `2px solid ${isSelected ? communityStyle.mainColor : "transparent"}`,
                    borderRadius: 8,
                    position: "relative" as const,
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    transform: isSelected ? "scale(1.05)" : "scale(1)",
                    boxShadow: isSelected ? `0 4px 12px ${communityStyle.mainColor}33` : "0 2px 4px rgba(0,0,0,0.1)",
                  };

                  const headerUrl = resolveHeaderUrl(theme.headerImage || "");

                  return (
                    <Grid item xs={6} sm={6} style={{ flexBasis: "20%", maxWidth: "20%" }} key={theme.id}>
                      <Box
                        style={themeCardStyle}
                        onClick={() => {
                          handleThemeSelect(theme.id);
                        }}
                      >
                        <div style={{ height: "50%", background: theme.mainColor }}>
                          {headerUrl ? (
                            <img
                              src={headerUrl}
                              alt=""
                              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                            />
                          ) : null}
                        </div>
                        <div
                          style={{
                            height: "50%",
                            backgroundColor: theme.secondaryColor,
                            color: invertColor(theme.secondaryColor),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 11,
                            fontWeight: 600,
                            padding: "0 4px",
                            textAlign: "center",
                          }}
                        >
                          {theme.name}
                        </div>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {/* CUSTOM THEME CONTROLS */}
            {themeTab === 1 && (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Main Color"
                      type="color"
                      fullWidth
                      value={communityStyle.mainColor}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSelectedThemeId("custom");
                        setCommunityStyle((s) => ({ ...s, mainColor: v }));
                      }}
                      InputLabelProps={{ shrink: true, style: { color: invertedSecondary } }}
                      InputProps={{ style: { height: 48, padding: "8px", background: "transparent" } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Secondary Color"
                      type="color"
                      fullWidth
                      value={communityStyle.secondaryColor}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSelectedThemeId("custom");
                        setCommunityStyle((s) => ({ ...s, secondaryColor: v }));
                      }}
                      InputLabelProps={{ shrink: true, style: { color: invertedSecondary } }}
                      InputProps={{ style: { height: 48, padding: "8px", background: "transparent" } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Font Style"
                      fullWidth
                      value={communityStyle.fontStyle}
                      onChange={(e) => {
                        setSelectedThemeId("custom");
                        setCommunityStyle((s) => ({ ...s, fontStyle: e.target.value }));
                      }}
                      InputLabelProps={{ style: { color: invertedSecondary } }}
                      InputProps={{ style: { color: invertedSecondary } }}
                    />
                  </Grid>

                  {/* Header image upload + preview */}
                  <Grid item xs={12}>
                    <Box p={2} style={{ border: `1px dashed ${invertedSecondary}55`, borderRadius: 8 }}>
                      <Typography variant="body2" style={{ color: `${invertedSecondary}cc`, fontWeight: 600 }}>
                        Header Image
                      </Typography>

                      <Box mt={1} display="flex" alignItems="center" style={{ gap: 12 }}>
                        <input
                          ref={headerFileInputRef}
                          type="file"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={onHeaderFilePicked}
                        />
                        <Button
                          variant="outlined"
                          onClick={() => headerFileInputRef.current?.click()}
                          disabled={headerUploading || !idsReady}
                          title={!idsReady ? "Program/community not loaded yet" : ""}
                          style={{ color: communityStyle.mainColor, borderColor: communityStyle.mainColor }}
                        >
                          {headerUploading ? "Uploading…" : "Choose Header Image"}
                        </Button>
                      </Box>

                      {headerUploadMsg && (
                        <Typography variant="caption" style={{ color: `${invertedSecondary}99` }}>
                          {headerUploadMsg}
                        </Typography>
                      )}

                      {/* Small preview strip */}
                      <Box
                        mt={2}
                        style={{ borderRadius: 8, overflow: "hidden", border: `1px solid ${invertedSecondary}33`, height: 64 }}
                      >
                        {headerImageUrl ? (
                          <img
                            src={headerImageUrl}
                            alt="header preview"
                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                          />
                        ) : (
                          <div style={{ width: "100%", height: "100%", background: communityStyle.mainColor }} />
                        )}
                      </Box>
                    </Box>
                  </Grid>

                  {/* LIVE PREVIEW */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" style={{ color: invertedSecondary, marginBottom: 8 }}>
                      Preview
                    </Typography>
                    <Box
                      style={{
                        borderRadius: 8,
                        overflow: "hidden",
                        boxShadow: `0 4px 12px ${invertColor(communityStyle.secondaryColor)}22`,
                        border: `1px solid ${invertedSecondary}33`,
                      }}
                    >
                      <Box style={{ padding: 12, display: "flex", alignItems: "center", gap: 10, background: communityStyle.secondaryColor }}>
                        <div style={{ width: 40, height: 40, borderRadius: 10, overflow: "hidden", border: `1px solid ${invertedSecondary}33` }}>
                          <img
                            src={logoPreviewUrl || logoImageUrl || resolveHeaderUrl(appLogo)}
                            alt="logo"
                            style={{ width: "100%", height: "100%", objectFit: "contain", display: "block", background: communityStyle.secondaryColor }}
                          />
                        </div>
                        <Typography variant="subtitle2" style={{ color: invertColor(communityStyle.secondaryColor) }}>
                          {communityIdentifier}
                        </Typography>
                      </Box>

                      <div style={{ height: 64, background: communityStyle.mainColor }}>
                        {headerImageUrl ? (
                          <img
                            src={headerImageUrl}
                            alt="header"
                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                          />
                        ) : null}
                      </div>
                      <Box
                        style={{
                          padding: 16,
                          background: communityStyle.secondaryColor,
                          color: invertColor(communityStyle.secondaryColor),
                          fontFamily: communityStyle.fontStyle,
                        }}
                      >
                        <Typography variant="body2">Body text preview</Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>

          {/* SAVE */}
          {saveError && (
            <Typography variant="body2" style={{ color: "#d32f2f", marginTop: 8 }}>
              {saveError}
            </Typography>
          )}
          <Box mt={4}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSave}
              disabled={saving || headerUploading || isBlobUrl(communityStyle.headerImage)}
              style={{ backgroundColor: communityStyle.mainColor, color: communityStyle.secondaryColor }}
              title={headerUploading || isBlobUrl(communityStyle.headerImage) ? "Wait for header upload to finish" : ""}
            >
              {saving ? "Saving…" : (headerUploading || isBlobUrl(communityStyle.headerImage)) ? "Uploading header…" : "Save Changes"}
            </Button>
          </Box>
        </MuiCard>
      </div>
    </>
  );
}
